import React, { useState } from "react";
import { Hospital, HospitalUpdatePayload } from "../../types/hospital.types";
import { Button, Dialog, Field, Input } from "../ui";
import { t } from "../../i18n";

interface HospitalEditModalProps {
  hospital: Hospital;
  saving: boolean;
  onConfirm: (payload: HospitalUpdatePayload) => void;
  onClose: () => void;
}

const HospitalEditModal: React.FC<HospitalEditModalProps> = ({ hospital, saving, onConfirm, onClose }) => {
  const [name, setName] = useState(hospital.name);
  const [email, setEmail] = useState(hospital.email);

  const buildPayload = (): HospitalUpdatePayload => {
    const payload: HospitalUpdatePayload = {};
    const trimmedName = name.trim();
    const trimmedEmail = email.trim().toLowerCase();
    if (trimmedName && trimmedName !== hospital.name) payload.name = trimmedName;
    if (trimmedEmail && trimmedEmail !== hospital.email.toLowerCase()) payload.email = trimmedEmail;
    return payload;
  };

  // Diferente do fluxo de empresas: aqui `name` e `email` já são obrigatórios
  // na criação, então normalmente já vêm preenchidos. Por isso os dois campos
  // ficam sempre editáveis (ao invés de só os que estiverem vazios) — o
  // modal serve para corrigir um dado errado, não para completar um faltante.
  const hasChanges = Object.keys(buildPayload()).length > 0;
  const canSubmit = !saving && !!name.trim() && !!email.trim();

  return (
    <Dialog
      open
      size="sm"
      onClose={onClose}
      closeDisabled={saving}
      title={t("links.hospital.reviewTitle")}
      description={t("links.hospital.reviewText")}
      footer={
        <>
          <Button variant="outline" onClick={() => onConfirm({})} disabled={saving}>
            {t("links.linkOnly")}
          </Button>
          <Button variant="primary" onClick={() => onConfirm(buildPayload())} disabled={!canSubmit || !hasChanges}>
            {saving ? t("links.saving") : t("links.saveAndLink")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t("links.name")}>
          <Input
            type="text"
            value={name}
            disabled={saving}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("links.hospital.nameField")}
          />
        </Field>
        <Field label={t("links.hospital.email")}>
          <Input
            type="email"
            value={email}
            disabled={saving}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("links.hospital.emailPlaceholder")}
          />
        </Field>
      </div>
    </Dialog>
  );
};

export default HospitalEditModal;
